import React, { useRef, useState, useEffect } from 'react';
import TicketBlock from './TicketBlock';
import TextareaAutosize from 'react-textarea-autosize';
import { geocodeAddress } from '../../helpers/AI_functions';
import { getFirestore, doc, setDoc, Timestamp } from 'firebase/firestore';
import './ChatComponent.css';

function ModelBuilderChatComponent({ state, updateState, tickets = [], chatId }) {
  const [showNew, setShowNew] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [savingTicket, setSavingTicket] = useState(false);

  const ticketRefs = useRef({});
  const db = getFirestore();

  useEffect(() => {
    const tk = state.focusTicket;
    if (tk && ticketRefs.current[tk.id]) {
      ticketRefs.current[tk.id].scrollIntoView({ behavior: 'smooth' });
    }
  }, [state.focusTicket]);

  const saveComment = async (tkId, text) => {
    if (!text.trim()) return;
    const ref = doc(
      db,
      'model_builders',
      state.selectedSimulationId,
      'model_tickets',
      tkId
    );
    await setDoc(
      ref,
      {
        id: tkId,
        comments_array: state.ticketComments?.[tkId]
          ? [...state.ticketComments[tkId], text.trim()]
          : [text.trim()],
        status: 'commented'
      },
      { merge: true }
    );
  };

  const sortedTickets = React.useMemo(() => {
    return [...tickets].sort((a, b) => {
      const aTs = a.comments_array?.reduce((mx, c) => Math.max(mx, c.ts), 0) || 0;
      const bTs = b.comments_array?.reduce((mx, c) => Math.max(mx, c.ts), 0) || 0;
      return bTs - aTs;
    });
  }, [tickets]);

  return (
    <div
      className="outer-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1E1E1E',
        color: '#FFFFFF',
        padding: 16,
        borderRadius: 8,
        height: '100%',
        overflowY: 'auto'
      }}
    >
      {/* ────── “NEW TICKET” BUTTON ────── */}
      <button
        onClick={() => setShowNew(true)}
        style={{
          alignSelf: 'flex-start',
          marginBottom: 16,
          padding: '6px 14px',
          border: 'none',
          borderRadius: 4,
          background: '#22c55e',
          color: '#000',
          fontWeight: 600,
          cursor: 'pointer'
        }}
      >
        + New ticket
      </button>

      {/* ────── MODAL ────── */}
      {showNew && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => !savingTicket && setShowNew(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90%',
              maxWidth: 460,
              background: '#262626',
              padding: 24,
              borderRadius: 8,
              boxShadow: '0 10px 25px rgba(0,0,0,.4)'
            }}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 20 }}>Create ticket</h3>

            <TextareaAutosize
              minRows={3}
              placeholder="Comment…"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              style={{
                width: '100%',
                marginBottom: 12,
                borderRadius: 4,
                border: '1px solid #555',
                background: '#1E1E1E',
                color: '#FFF',
                padding: 8,
                resize: 'vertical'
              }}
            />

            <input
              placeholder="Address…"
              value={newAddress}
              onChange={(e) => setNewAddress(e.target.value)}
              style={{
                width: '100%',
                marginBottom: 18,
                padding: 8,
                borderRadius: 4,
                border: '1px solid #555',
                background: '#1E1E1E',
                color: '#FFF'
              }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                disabled={savingTicket}
                onClick={async () => {
                  if (!newAddress.trim()) return;
                  try {
                    setSavingTicket(true);
                    const { lat, lng } = await geocodeAddress(
                      newAddress,
                      '',
                      ''
                    );
                    console.log(lat,lng)
                    const ticketId = `other_${Date.now()}`;
                    await setDoc(
                      doc(
                        db,
                        'model_builders',
                        state.selectedSimulationId,
                        'model_tickets',
                        ticketId
                      ),
                      {
                        id: ticketId,
                        type: 'other',
                        address: newAddress.trim(),
                        latitude: +lat,
                        longitude: +lng,
                        status: 'commented',
                        comments_array: newComment.trim()
                          ? [
                              {
                                text: newComment.trim(),
                                ts: Timestamp.now().toMillis()
                              }
                            ]
                          : []
                      }
                    );
                    setShowNew(false);
                    setNewComment('');
                    setNewAddress('');
                  } catch (err) {
                    console.error('Could not create ticket:', err);
                  } finally {
                    setSavingTicket(false);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  border: 'none',
                  borderRadius: 4,
                  background: '#22c55e',
                  color: '#000',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {savingTicket ? 'Saving…' : 'Save'}
              </button>

              <button
                disabled={savingTicket}
                onClick={() => setShowNew(false)}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  border: 'none',
                  borderRadius: 4,
                  background: '#525252',
                  color: '#FFF',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────── TICKET LIST ────── */}
      {tickets.length === 0 && (
        <div style={{ color: '#AAA' }}>No tickets yet.</div>
      )}

      {sortedTickets.map((tk) => (
        <div
          key={tk.id}
          ref={(el) => (ticketRefs.current[tk.id] = el)}
          style={{ marginBottom: 12 }}
        >
          <TicketBlock
            ticket={tk}
            onSelect={() => updateState({ focusTicket: tk })}
            onSaveComment={saveComment}
          />
        </div>
      ))}
    </div>
  );
}

export default ModelBuilderChatComponent;